import React, {useEffect, useRef, useState} from "react";
import {useEditor, EditorContent} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextStyle from "@tiptap/extension-text-style";
import FontFamily from "@tiptap/extension-font-family";
import FontSize from "./FontSize.js";
import BoldWithInlineStyle from "./Bold.js";
import ResizableImage from "./ResizableImage.jsx";
import {ButtonGroup, Button, Form} from "react-bootstrap";
import axios from "axios";
import {
    PiTextBBold,
    PiTextItalic,
    PiTextUnderline,
    PiLink,
    PiListBullets,
    PiListNumbers,
    PiTextHOne,
    PiImage,
} from "react-icons/pi";

const PLACEHOLDER_CHIPS = [
    {token: "{{anrede}}", label: "Anrede"},
    {token: "{{schulname}}", label: "Schulname"},
    {token: "{{email}}", label: "E-Mail"},
    {token: "{{stadt}}", label: "Stadt"},
];

// Standard-Schriftart/-größe der Mail - muss deckungsgleich mit dem
// Arial-11px-Wrapper aus CampaignMailBuilder::render() sein, damit der
// Editor exakt so aussieht wie die spätere Mail.
const DEFAULT_FONT_FAMILY = "Arial, Helvetica, sans-serif";
const DEFAULT_FONT_SIZE = "12px";

const FONT_FAMILIES = [
    {label: "Arial", value: "Arial, Helvetica, sans-serif"},
    {label: "Helvetica", value: "Helvetica, Arial, sans-serif"},
    {label: "Verdana", value: "Verdana, Geneva, sans-serif"},
    {label: "Tahoma", value: "Tahoma, Geneva, sans-serif"},
    {label: "Georgia", value: "Georgia, 'Times New Roman', serif"},
    {label: "Times New Roman", value: "'Times New Roman', Times, serif"},
    {label: "Courier New", value: "'Courier New', Courier, monospace"},
    {label: "Calibri", value: "Calibri, Candara, Segoe, sans-serif"},
];

const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24];

/**
 * WYSIWYG-Editor auf Basis Tiptap für Kampagnen-HTML (Mail-Body und
 * Signaturen). Kontrollierte Komponente: value/onChange liefern/erwarten
 * einen HTML-String, damit sie direkt in ein Inertia-useForm-Feld passt.
 * Die eigentliche Sicherheits-Bereinigung passiert serverseitig
 * (CampaignHtmlSanitizer) - hier geht es nur um die Eingabe.
 */
export default function HtmlEditor({value, onChange, placeholders}) {
    const chips = placeholders
        ? placeholders.map((token) => ({token, label: token.replace(/[{}]/g, "")}))
        : PLACEHOLDER_CHIPS;

    const [uploadError, setUploadError] = useState(null);
    const [uploading, setUploading] = useState(false);
    const imageInputRef = useRef(null);

    // Erzwingt einen Re-Render bei reinen Selektions-/Cursor-Bewegungen
    // (ohne Inhaltsänderung) - ohne das würden die Schriftart-/Schriftgrößen-
    // Auswahlfelder den Wert an der Cursorposition nicht aktualisieren, weil
    // onUpdate dafür nicht feuert.
    const [, forceRender] = useState(0);

    const editor = useEditor({
        extensions: [
            // bold aus dem StarterKit deaktiviert - stattdessen unten die
            // eigene BoldWithInlineStyle-Extension, die zusätzlich ein
            // hartes inline font-weight:700 mitgibt (siehe Bold.js).
            StarterKit.configure({bold: false}),
            BoldWithInlineStyle,
            Underline,
            Link.configure({openOnClick: false}),
            TextStyle,
            FontFamily,
            FontSize,
            ResizableImage.configure({inline: false, allowBase64: false}),
        ],
        content: value || "",
        onUpdate: ({editor}) => {
            onChange(editor.getHTML());
        },
        onSelectionUpdate: () => {
            forceRender((n) => n + 1);
        },
    });

    // Von außen gesetzte Werte (z. B. beim Laden einer bestehenden Kampagne)
    // in den Editor übernehmen, ohne bei jedem Tastendruck neu zu setzen.
    useEffect(() => {
        if (editor && value !== undefined && value !== editor.getHTML()) {
            editor.commands.setContent(value || "", false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, editor]);

    if (!editor) {
        return null;
    }

    const insertPlaceholder = (token) => {
        editor.chain().focus().insertContent(token).run();
    };

    const currentFontFamily = editor.getAttributes("textStyle").fontFamily || DEFAULT_FONT_FAMILY;
    const currentFontSize = editor.getAttributes("textStyle").fontSize || DEFAULT_FONT_SIZE;

    const handleFontFamilyChange = (e) => {
        editor.chain().focus().setFontFamily(e.target.value).run();
    };

    const handleFontSizeChange = (e) => {
        editor.chain().focus().setFontSize(e.target.value).run();
    };

    const setLink = () => {
        const url = window.prompt("Link-URL:");
        if (url === null) {
            return;
        }
        if (url === "") {
            editor.chain().focus().unsetLink().run();
            return;
        }
        editor.chain().focus().setLink({href: url}).run();
    };

    const triggerImageUpload = () => {
        setUploadError(null);
        imageInputRef.current?.click();
    };

    const handleImageSelected = async (e) => {
        const file = e.target.files?.[0] || null;
        e.target.value = ""; // dieselbe Datei erneut auswaehlbar machen

        if (!file) {
            return;
        }

        setUploading(true);
        setUploadError(null);
        try {
            const form = new FormData();
            form.append("image", file);
            const {data} = await axios.post("/campaigns/images", form);
            editor.chain().focus().setImage({src: data.url}).run();
        } catch (err) {
            setUploadError(
                err.response?.data?.errors?.image?.[0]
                || err.response?.data?.message
                || "Bild konnte nicht hochgeladen werden."
            );
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="html-editor border rounded">
            <div className="html-editor-toolbar d-flex flex-wrap gap-2 p-2 border-bottom bg-light">
                <Form.Select
                    size="sm"
                    style={{width: "auto"}}
                    value={currentFontFamily}
                    onChange={handleFontFamilyChange}
                    title="Schriftart"
                >
                    {FONT_FAMILIES.map((font) => (
                        <option key={font.value} value={font.value}>
                            {font.label}
                        </option>
                    ))}
                </Form.Select>
                <Form.Select
                    size="sm"
                    style={{width: "auto"}}
                    value={currentFontSize}
                    onChange={handleFontSizeChange}
                    title="Schriftgröße"
                >
                    {FONT_SIZES.map((size) => (
                        <option key={size} value={`${size}px`}>
                            {size}px
                        </option>
                    ))}
                </Form.Select>
                <ButtonGroup size="sm">
                    <Button
                        variant={editor.isActive("bold") ? "secondary" : "outline-secondary"}
                        onClick={() => editor.chain().focus().toggleBold().run()}
                    >
                        <PiTextBBold/>
                    </Button>
                    <Button
                        variant={editor.isActive("italic") ? "secondary" : "outline-secondary"}
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                    >
                        <PiTextItalic/>
                    </Button>
                    <Button
                        variant={editor.isActive("underline") ? "secondary" : "outline-secondary"}
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                    >
                        <PiTextUnderline/>
                    </Button>
                    <Button
                        variant={editor.isActive("link") ? "secondary" : "outline-secondary"}
                        onClick={setLink}
                    >
                        <PiLink/>
                    </Button>
                </ButtonGroup>
                <ButtonGroup size="sm">
                    <Button
                        variant={editor.isActive("heading", {level: 2}) ? "secondary" : "outline-secondary"}
                        onClick={() => editor.chain().focus().toggleHeading({level: 2}).run()}
                    >
                        <PiTextHOne/>
                    </Button>
                    <Button
                        variant={editor.isActive("bulletList") ? "secondary" : "outline-secondary"}
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                    >
                        <PiListBullets/>
                    </Button>
                    <Button
                        variant={editor.isActive("orderedList") ? "secondary" : "outline-secondary"}
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    >
                        <PiListNumbers/>
                    </Button>
                </ButtonGroup>
                <ButtonGroup size="sm">
                    <Button
                        variant="outline-secondary"
                        onClick={triggerImageUpload}
                        disabled={uploading}
                        title="Bild einfügen"
                    >
                        <PiImage/>
                    </Button>
                </ButtonGroup>
                <ButtonGroup size="sm">
                    {chips.map((chip) => (
                        <Button
                            key={chip.token}
                            variant="outline-primary"
                            onClick={() => insertPlaceholder(chip.token)}
                        >
                            {chip.label}
                        </Button>
                    ))}
                </ButtonGroup>
            </div>
            <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                className="d-none"
                onChange={handleImageSelected}
            />
            {uploadError && (
                <div className="alert alert-danger m-2 mb-0 py-1 px-2 small">{uploadError}</div>
            )}
            <div className="html-editor-content p-3">
                <EditorContent editor={editor}/>
            </div>
        </div>
    );
}
